import { ref } from 'vue';

const SCRIPT_ID = '__RECAPTCHA_SCRIPT';
const ONLOAD_CALLBACK = '__recaptchaOnload';

let readyPromise: Promise<void> | null = null;

function ensureScript(): Promise<void> {
    if (readyPromise) {
        return readyPromise;
    }

    readyPromise = new Promise<void>((resolve, reject) => {
        (window as unknown as Record<string, unknown>)[ONLOAD_CALLBACK] = resolve;

        if (!document.getElementById(SCRIPT_ID)) {
            const script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.src = `https://www.google.com/recaptcha/api.js?onload=${ONLOAD_CALLBACK}&render=explicit&hl=en`;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                readyPromise = null;
                script.remove();
                reject(new Error('Failed to load reCAPTCHA'));
            };
            document.head.appendChild(script);
        }
    });

    return readyPromise;
}

export function _resetRecaptchaState(): void {
    readyPromise = null;
}

export function useRecaptcha(siteKey: string) {
    const widgetId = ref<number | null>(null);
    const enabled = siteKey !== '';

    let verifyResolve: ((token: string) => void) | null = null;
    let verifyReject: ((error: Error) => void) | null = null;

    const onVerify = (token: string) => {
        verifyResolve?.(token);
        verifyResolve = null;
        verifyReject = null;
    };

    const onExpiredOrError = () => {
        verifyReject?.(new Error('reCAPTCHA challenge failed'));
        verifyResolve = null;
        verifyReject = null;
    };

    const render = (container: HTMLElement): void => {
        if (!enabled) {
            return;
        }

        ensureScript()
            .then(() => {
                widgetId.value = window.grecaptcha!.render(container, {
                    sitekey: siteKey,
                    size: 'invisible',
                    callback: onVerify,
                    'expired-callback': onExpiredOrError,
                    'error-callback': onExpiredOrError,
                });
            })
            .catch(() => {
                // Script failed to load — execute() will also reject via ensureScript()
            });
    };

    const execute = (): Promise<string | undefined> => {
        if (!enabled) {
            return Promise.resolve(undefined);
        }

        return new Promise<string | undefined>((resolve, reject) => {
            verifyResolve = resolve;
            verifyReject = reject;

            ensureScript()
                .then(() => {
                    if (widgetId.value !== null) {
                        window.grecaptcha!.execute(widgetId.value);
                    }
                })
                .catch(reject);
        });
    };

    const reset = (): void => {
        if (!enabled || widgetId.value === null) {
            return;
        }

        window.grecaptcha!.reset(widgetId.value);
    };

    return {
        enabled,
        render,
        execute,
        reset,
    };
}
